import React, { useState } from 'react';
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Mic as MicIcon,
  DisplaySettings as DisplayIcon,
  Notifications as NotificationsIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';
import VoiceSettings from './VoiceSettings';
import DisplaySettings from './DisplaySettings';
import NotificationSettings from './NotificationSettings';

// eslint-disable-next-line max-lines-per-function
const UserSettings = () => {
  const navigate = useNavigate();
  const { loading } = useUserPreferences();
  const [activeSection, setActiveSection] = useState('voice');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const sections = [
    { id: 'voice', label: 'Voice & Audio', icon: <MicIcon />, component: VoiceSettings },
    { id: 'display', label: 'Display', icon: <DisplayIcon />, component: DisplaySettings },
    { id: 'notifications', label: 'Notifications', icon: <NotificationsIcon />, component: NotificationSettings },
  ];

  const ActiveComponent = sections.find(s => s.id === activeSection)?.component;

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  // Mobile layout: horizontal tabs at top, content below
  if (isMobile) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100%' }}>
        {/* Header */}
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Go back">
            <IconButton onClick={() => navigate(-1)} size="small">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h6">Settings</Typography>
        </Box>

        {/* Horizontal tabs for section switching */}
        <Tabs
          value={activeSection}
          onChange={(e, newValue) => setActiveSection(newValue)}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontSize: '0.85rem',
            },
          }}
        >
          {sections.map((section) => (
            <Tab
              key={section.id}
              value={section.id}
              icon={section.icon}
              label={section.label}
              iconPosition="start"
              sx={{ gap: 0.5 }}
            />
          ))}
        </Tabs>

        {/* Content area */}
        <Box sx={{ p: 2 }}>
          {ActiveComponent && <ActiveComponent />}
        </Box>
      </Box>
    );
  }

  // Desktop layout: sidebar + content
  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: 'background.default' }}>
      {/* Sidebar */}
      <Paper
        sx={{
          width: 280,
          height: '100%',
          borderRadius: 0,
          borderRight: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column'
        }}
        elevation={0}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Go back">
            <IconButton onClick={() => navigate(-1)} size="small">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h6">Settings</Typography>
        </Box>

        <Divider />

        <List sx={{ flex: 1 }}>
          {sections.map((section) => (
            <ListItem key={section.id} disablePadding>
              <ListItemButton
                selected={activeSection === section.id}
                onClick={() => setActiveSection(section.id)}
              >
                <ListItemIcon>{section.icon}</ListItemIcon>
                <ListItemText primary={section.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Content Area */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
          {ActiveComponent && <ActiveComponent />}
        </Box>
      </Box>
    </Box>
  );
};

export default UserSettings;